import { GraphQLClient } from 'graphql-request'
import { getOperationId, setAuthenticatedUser } from './appInsights'
import { auth0 } from './auth0'
import { setAuthenticatedUserSentry } from './sentry'
import { useNotification } from './stores/notifications'

class TokenNotReadyError extends Error {
  constructor(message: string | undefined) {
    super(message)
    this.name = 'TokenNotReadyError'
  }
}

const client = new GraphQLClient(import.meta.env.VITE_PLATFORM_GRAPHQL_API_URL, {
  requestMiddleware: async (request) => {
    const readyToLoadToken = !auth0.isLoading.value && auth0.isAuthenticated.value
    if (!readyToLoadToken) {
      // Check out responseMiddleware to see how this is handled
      throw new TokenNotReadyError('Token not ready, request halted')
    }

    let token
    try {
      token = await auth0.getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
          scope: 'openid profile email'
        }
      })
    } catch (err) {
      const error = err as { error: string }
      if (['login_required', 'consent_required'].includes(error?.error)) {
        auth0.loginWithRedirect({
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            redirect_uri: import.meta.env.VITE_AUTH0_CALLBACK_URL,
            scope: 'openid profile email'
          }
        })
      }
      throw err
    }

    const user = auth0.user.value
    if (user?.sub) {
      setAuthenticatedUser(user.sub, user.email)
      setAuthenticatedUserSentry(user.sub, user.email)
    }

    const modifiedRequest = {
      ...request,
      headers: {
        ...request.headers,
        authorization: `Bearer ${token}`,
        customer_id: localStorage.getItem('klimate-customer-id') || '',
        'x-operation-id': getOperationId() ?? ''
      }
    }

    return modifiedRequest
  },
  responseMiddleware: (response) => {
    if (response instanceof TokenNotReadyError) {
      // Request interrupted early because of an unavailable token
      return
    }
    const notification = useNotification()
    if (response instanceof Error) {
      // Looks like HotChocolate and graphql-request disagree on how errors are typed 🤷‍♀️
      const errors = (response as { response?: { errors?: { message: string }[] } }).response
        ?.errors
      if (errors) {
        const message = errors[0]?.message ?? 'Internal server error.'
        notification.push(
          {
            type: 'error',
            message
          },
          20000
        )
        throw new Error(message)
      }
      throw response
    }
  }
})

export const request = client.request.bind(client)
